import { useEffect, useState } from "react";

/**
 * Height (in px) currently covered by the on-screen keyboard, derived from the
 * VisualViewport API. Returns 0 when no keyboard is open — or when the API is
 * unavailable (e.g. desktop), so callers can use it unconditionally.
 *
 * Subtract it from a `100dvh`-based panel height so the composer stays visible
 * above the keyboard instead of hiding behind it on iOS (where `dvh`/`vh` do
 * not shrink for the keyboard).
 */
export function useKeyboardInset(): number {
  const [inset, setInset] = useState(0);

  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;

    const update = () => {
      // The layout viewport minus the visible viewport (and any scroll offset)
      // is the slice hidden behind the keyboard. Clamp to >= 0 and ignore tiny
      // sub-pixel jitter so we don't churn re-renders.
      const next = Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
      setInset((prev) => (Math.abs(prev - next) > 1 ? next : prev));
    };

    update();
    vv.addEventListener("resize", update);
    vv.addEventListener("scroll", update);
    return () => {
      vv.removeEventListener("resize", update);
      vv.removeEventListener("scroll", update);
    };
  }, []);

  return inset;
}
